import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ShippingZone } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { CountrySelect, countryName } from '@/features/markets';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyZoneForm, fromZone, toZoneCreate, toZoneUpdate } from '../api/mappers';
import { useCreateZone, useUpdateZone } from '../hooks/use-shipping-mutations';
import { hasRegions } from '../schemas/regions';
import { takenCountries, zoneErrorTarget } from '../schemas/shipping-rules';
import {
  zoneFormSchema,
  type ZoneFormInput,
  type ZoneFormOutput,
} from '../schemas/zone-form-schema';

import { RegionPicker } from './region-picker';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['name', 'countries', 'regions', 'position']);

export interface ZoneDialogProps {
  storeId: string;
  zones: readonly ShippingZone[];
  /** null creates; a zone edits it. */
  zone: ShippingZone | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Name, countries and position. A country already in another zone cannot be picked. */
export function ZoneDialog({ storeId, zones, zone, open, onOpenChange }: ZoneDialogProps) {
  const { t } = useTranslation(['shipping', 'common']);
  const create = useCreateZone(storeId);
  const update = useUpdateZone(storeId);
  const message = useErrorMessage();
  const nextPosition = zones.reduce((max, z) => Math.max(max, z.position + 1), 0);
  const form = useForm<ZoneFormInput, unknown, ZoneFormOutput>({
    resolver: useI18nResolver(zoneFormSchema),
    defaultValues: zone ? fromZone(zone) : emptyZoneForm(nextPosition),
  });
  const isPending = create.isPending || update.isPending;
  const taken = takenCountries(zones, zone?.id);
  const countries = form.watch('countries');

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      if (zone) await update.mutateAsync({ id: zone.id, body: toZoneUpdate(values) });
      else await create.mutateAsync(toZoneCreate(values));
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const target = zoneErrorTarget(error);
      if (target) {
        const countries = Array.isArray(target.values?.countries)
          ? target.values.countries.map((c) => countryName(String(c))).join(', ')
          : undefined;
        form.setError(target.field, { type: 'server', message: t(target.key, { countries }) });
      } else {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {zone ? t('shipping:zone.editDialog.title') : t('shipping:zone.create.title')}
          </DialogTitle>
          <DialogDescription>{t('shipping:zone.create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping:zone.fields.name')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('shipping:zone.fields.position')}</FormLabel>
                    <FormControl>
                      <Input type="number" inputMode="numeric" min={0} step={1} {...field} />
                    </FormControl>
                    <FormDescription>{t('shipping:zone.fields.positionHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="countries"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('shipping:zone.fields.countries')}</FormLabel>
                  <FormControl>
                    <CountrySelect
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      taken={taken}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="regions"
              render={({ field }) => (
                <FormItem>
                  {countries.some((c) => hasRegions(c)) ? (
                    <>
                      <FormLabel>{t('shipping:zone.fields.regions')}</FormLabel>
                      <FormDescription>{t('shipping:zone.fields.regionsHint')}</FormDescription>
                    </>
                  ) : null}
                  <RegionPicker
                    countries={countries}
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : zone
                    ? t('common:actions.save')
                    : t('shipping:zone.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
